import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
  Unlink,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import { toEditorHtml } from "@/components/legal/legalContentUtils";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

type TLegalRichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Fill parent flex column; scroll inside editor body. */
  fillHeight?: boolean;
};

type TToolbarButtonProps = {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  label: string;
  children: ReactNode;
};

function ToolbarButton({ onClick, isActive, disabled, label, children }: TToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant={isActive ? "secondary" : "ghost"}
      size="icon"
      className={cn("h-8 w-8 shrink-0", isActive && "bg-primary/10 text-primary hover:bg-primary/15")}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {children}
    </Button>
  );
}

export function LegalRichTextEditor({
  value,
  onChange,
  onBlur,
  disabled = false,
  placeholder = "Start writing your legal document...",
  className,
  fillHeight = false,
}: TLegalRichTextEditorProps) {
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline underline-offset-4",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: toEditorHtml(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class:
          "legal-rich-text-editor__content prose prose-sm max-w-none focus:outline-none px-4 py-3 sm:px-5 sm:py-4",
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      isInternalUpdate.current = true;
      const html = currentEditor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    onBlur: () => {
      onBlur?.();
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor || isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const nextHtml = toEditorHtml(value);
    const currentHtml = editor.getHTML();
    if (nextHtml !== currentHtml && toEditorHtml(currentHtml) !== nextHtml) {
      editor.commands.setContent(nextHtml, { emitUpdate: false });
    }
  }, [editor, value]);

  const setLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Enter URL", previousUrl ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  if (!editor) {
    return (
      <div
        className={cn(
          "rounded-xl border border-input min-h-[16rem] animate-pulse bg-muted/30",
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        "legal-rich-text-editor flex flex-col overflow-hidden rounded-xl border border-input bg-background shadow-sm",
        fillHeight && "min-h-0 flex-1",
        disabled && "opacity-60",
        className
      )}
    >
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-border bg-muted/40 p-2">
        <ToolbarButton
          label="Heading 1"
          isActive={editor.isActive("heading", { level: 1 })}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Heading 2"
          isActive={editor.isActive("heading", { level: 2 })}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Heading 3"
          isActive={editor.isActive("heading", { level: 3 })}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <div className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
        <ToolbarButton
          label="Bold"
          isActive={editor.isActive("bold")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          isActive={editor.isActive("italic")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Strikethrough"
          isActive={editor.isActive("strike")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-4 w-4" />
        </ToolbarButton>
        <div className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
        <ToolbarButton
          label="Bullet list"
          isActive={editor.isActive("bulletList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          isActive={editor.isActive("orderedList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Blockquote"
          isActive={editor.isActive("blockquote")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <div className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
        <ToolbarButton label="Add link" disabled={disabled} onClick={setLink}>
          <Link2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Remove link"
          disabled={disabled || !editor.isActive("link")}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          <Unlink className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <div
        className={cn(
          "legal-document-scroll min-h-0 overflow-y-auto overscroll-contain",
          fillHeight ? "flex-1" : "min-h-[20rem] max-h-[min(42rem,calc(100vh-11rem))]"
        )}
      >
        <EditorContent editor={editor} className="min-h-[12rem]" />
      </div>
    </div>
  );
}
